/**
 * Shell do app (U1): sidebar com grupos/ícones e topbar com o nome + seletor
 * GLOBAL da competência atual (atalho para a página dela). O roteador por hash
 * permanece — isto é só a moldura visual.
 */
import { useEffect, useState } from "react";
import { AppShell, Burger, NavLink, Select, Text, Group, ThemeIcon, ScrollArea } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconHome,
  IconCalendarTime,
  IconUsers,
  IconListDetails,
  IconReceiptTax,
  IconClockHour4,
  IconCash,
  IconAdjustments,
  IconHistory,
  IconGift,
  IconBeach,
  IconUsersGroup,
  IconCoins,
  IconUserOff,
  IconRefresh,
  IconSettings,
  IconCpu,
} from "@tabler/icons-react";
import { api } from "../api";
import { navegar } from "../roteador";
import { fmtCompetencia } from "../format";

interface CompetenciaResumo {
  id: number;
  estado: string;
}

const GRUPOS: { rotulo: string; itens: { href: string; rotulo: string; icone: typeof IconHome }[] }[] = [
  {
    rotulo: "Operação",
    itens: [{ href: "/competencias", rotulo: "Competências", icone: IconCalendarTime }],
  },
  {
    rotulo: "Processamentos",
    itens: [
      { href: "/ferias", rotulo: "Férias", icone: IconBeach },
      { href: "/ferias-coletivas", rotulo: "Férias coletivas", icone: IconUsersGroup },
      { href: "/decimo", rotulo: "13º salário", icone: IconCoins },
      { href: "/rescisao", rotulo: "Rescisão", icone: IconUserOff },
    ],
  },
  {
    rotulo: "Cadastros",
    itens: [
      { href: "/funcionarios", rotulo: "Funcionários", icone: IconUsers },
      { href: "/beneficios", rotulo: "Benefícios", icone: IconGift },
      { href: "/catalogo", rotulo: "Eventos & Fórmulas", icone: IconListDetails },
      { href: "/tomadores", rotulo: "Tomadores (retenção NF)", icone: IconReceiptTax },
    ],
  },
  {
    rotulo: "Benefícios",
    itens: [
      { href: "/banco-horas", rotulo: "Banco de horas", icone: IconClockHour4 },
      { href: "/emprestimos", rotulo: "Empréstimos", icone: IconCash },
    ],
  },
  {
    rotulo: "Integrações",
    itens: [
      { href: "/integracoes/convenia", rotulo: "Convenia", icone: IconRefresh },
      { href: "/integracoes/tiquetaque", rotulo: "Tique Taque (ponto)", icone: IconClockHour4 },
    ],
  },
  {
    rotulo: "Sistema",
    itens: [
      { href: "/parametros", rotulo: "Parâmetros", icone: IconAdjustments },
      { href: "/perfis", rotulo: "Perfis de processamento", icone: IconCpu },
      { href: "/auditoria", rotulo: "Auditoria", icone: IconHistory },
      { href: "/admin", rotulo: "Administração", icone: IconSettings },
    ],
  },
];

export function Shell({ partes, children }: { partes: string[]; children: React.ReactNode }) {
  const [competencias, setCompetencias] = useState<CompetenciaResumo[]>([]);
  // sidebar colapsável em telas estreitas (polimento U5)
  const [menuAberto, { toggle: alternarMenu, close: fecharMenu }] = useDisclosure();

  useEffect(() => {
    fecharMenu();
    api
      .get<{ competencias: CompetenciaResumo[] }>("/competencias")
      .then((r) => setCompetencias([...r.competencias].sort((a, b) => b.id - a.id)))
      .catch(() => undefined);
    // recarrega quando navega (uma competência nova pode ter sido aberta)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partes.join("/")]);

  const rota = `/${partes.join("/")}`;
  const competenciaNaRota = partes[0] === "competencias" && partes[1] ? partes[1] : "";
  const atual = competencias.find((c) => c.estado !== "fechada") ?? competencias[0];

  return (
    <AppShell header={{ height: 52 }} navbar={{ width: 240, breakpoint: "sm", collapsed: { mobile: !menuAberto } }} padding="md">
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Burger opened={menuAberto} onClick={alternarMenu} hiddenFrom="sm" size="sm" aria-label="Alternar menu" />
            <Text fw={700} size="lg" c="sillis.7">
              Folha SilliS
            </Text>
            <NavLink
              component="a"
              href="#/"
              label="Início"
              leftSection={<IconHome size={16} />}
              active={partes.length === 0}
              variant="subtle"
              w="auto"
              style={{ borderRadius: 6 }}
            />
          </Group>
          <Select
            aria-label="Competência atual"
            placeholder="Competência…"
            size="sm"
            w={140}
            data={competencias.map((c) => ({
              value: String(c.id),
              label: `${fmtCompetencia(c.id)}${c.id === atual?.id ? " (atual)" : ""}`,
            }))}
            value={competenciaNaRota || null}
            onChange={(v) => v && navegar(`/competencias/${v}`)}
          />
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="xs">
        <ScrollArea>
          {GRUPOS.map((g) => (
            <NavLink
              key={g.rotulo}
              label={<Text size="xs" fw={700} c="dimmed" tt="uppercase">{g.rotulo}</Text>}
              childrenOffset={0}
              defaultOpened
              style={{ cursor: "default" }}
            >
              {g.itens.map((item) => (
                <NavLink
                  key={item.href}
                  component="a"
                  href={`#${item.href}`}
                  label={item.rotulo}
                  leftSection={
                    <ThemeIcon variant="light" size="sm" c={rota.startsWith(item.href) ? "sillis.7" : "gray"}>
                      <item.icone size={16} />
                    </ThemeIcon>
                  }
                  active={rota === item.href || (item.href !== "/competencias" && rota.startsWith(item.href))}
                  style={{ borderRadius: 6, marginBottom: 2 }}
                />
              ))}
            </NavLink>
          ))}
        </ScrollArea>
      </AppShell.Navbar>

      <AppShell.Main>
        <div style={{ maxWidth: 1280 }}>{children}</div>
      </AppShell.Main>
    </AppShell>
  );
}
